import { RuntimeActionType } from './toolViewerRuntimeAlerts';

type JsonPrimitive = string | number | boolean | null;

type RecordPropertySchema = {
  type?: string | string[];
  format?: string;
  enum?: unknown[];
  items?: RecordPropertySchema;
  // permite campos extras (ex.: x-ui-type, description) sem quebrar tipagem
  [key: string]: unknown;
};

type RecordPayloadSchema = {
  type?: string;
  properties?: Record<string, RecordPropertySchema>;
  required?: string[];
};

type RecordEntitySchema = {
  name?: string;
  entity_type?: string;
  payload_schema?: RecordPayloadSchema;
};

type ToolSchema = {
  entities?: RecordEntitySchema[];
  presentation_spec?: {
    entities?: RecordEntitySchema[];
  };
};

const COERCIBLE_RECORD_ACTIONS: ReadonlySet<RuntimeActionType> = new Set<RuntimeActionType>([
  'record.create',
  'record.update',
]);

const BOOLEAN_TRUE_TOKENS = new Set(['true', '1', 'yes', 'on', 'sim', 'y']);
const BOOLEAN_FALSE_TOKENS = new Set(['false', '0', 'no', 'off', 'nao', 'não', 'n']);

const stripNumericMask = (raw: string): string => {
  let cleaned = raw.replace(/[^0-9.,\-eE]/g, '').trim();
  if (!cleaned) {
    return cleaned;
  }
  // Normaliza separadores BR ("1.234,56") e padroniza ponto decimal.
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && hasDot) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    cleaned = cleaned.replace(',', '.');
  }
  return cleaned;
};

const coerceToNumber = (value: unknown): number | null | unknown => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const stripped = stripNumericMask(trimmed);
    if (!stripped) return null;
    const parsed = Number(stripped);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
};

const coerceToInteger = (value: unknown): number | null | unknown => {
  const numeric = coerceToNumber(value);
  if (numeric === null) return null;
  if (typeof numeric !== 'number') return numeric;
  return Math.trunc(numeric);
};

const coerceToBoolean = (value: unknown): boolean | null | unknown => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    if (!lowered) return null;
    if (BOOLEAN_TRUE_TOKENS.has(lowered)) return true;
    if (BOOLEAN_FALSE_TOKENS.has(lowered)) return false;
  }
  return value;
};

const coerceToArrayOfStrings = (value: unknown): unknown => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // ignora e cai no split CSV
      }
    }
    return trimmed
      .split(',')
      .map(token => token.trim())
      .filter(token => token.length > 0);
  }
  return value;
};

const pickPrimaryType = (type: string | string[] | undefined): string | undefined => {
  if (!type) return undefined;
  if (Array.isArray(type)) {
    return type.find(item => typeof item === 'string' && item !== 'null');
  }
  return type;
};

const coerceValueForSchema = (
  value: unknown,
  schema: RecordPropertySchema | undefined
): unknown => {
  if (!schema) return value;
  const primaryType = pickPrimaryType(schema.type);
  if (!primaryType) return value;

  switch (primaryType) {
    case 'number':
      return coerceToNumber(value);
    case 'integer':
      return coerceToInteger(value);
    case 'boolean':
      return coerceToBoolean(value);
    case 'array':
      return coerceToArrayOfStrings(value);
    case 'string':
      if (value === null || value === undefined) return value;
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return value;
    default:
      return value;
  }
};

const extractEntities = (toolSchema: unknown): RecordEntitySchema[] => {
  if (!toolSchema || typeof toolSchema !== 'object') return [];
  const schema = toolSchema as ToolSchema;
  if (Array.isArray(schema.entities) && schema.entities.length > 0) {
    return schema.entities;
  }
  const nested = schema.presentation_spec?.entities;
  if (Array.isArray(nested)) return nested;
  return [];
};

const findEntityByType = (
  entities: RecordEntitySchema[],
  entityType: string | undefined
): RecordEntitySchema | undefined => {
  if (!entityType) return undefined;
  const normalized = String(entityType).trim().toLowerCase();
  if (!normalized) return undefined;
  return entities.find(entity => {
    const name = String(entity?.name || entity?.entity_type || '').trim().toLowerCase();
    return name === normalized;
  });
};

/**
 * Aplica coerção determinística sobre o payload de `record.create` / `record.update`
 * antes de chamar o BFF. O backend valida via JSON Schema Draft-7 estrito, então
 * uma string `"29000"` é rejeitada quando o schema declara `type: number`. Aqui
 * tentamos converter o valor para o tipo esperado antes que a requisição saia
 * do frontend. Quando não houver schema (ferramentas sem `payload_schema`) ou
 * o tipo não for coercível, devolvemos o valor original.
 */
export const coerceRecordPayload = (input: {
  action: RuntimeActionType;
  entityType?: string;
  data: unknown;
  toolSchema?: unknown;
}): Record<string, unknown> => {
  const rawData =
    input.data && typeof input.data === 'object' && !Array.isArray(input.data)
      ? (input.data as Record<string, unknown>)
      : {};

  if (!COERCIBLE_RECORD_ACTIONS.has(input.action)) {
    return rawData;
  }
  const entities = extractEntities(input.toolSchema);
  if (entities.length === 0) {
    return rawData;
  }
  const entity = findEntityByType(entities, input.entityType);
  const properties = entity?.payload_schema?.properties;
  if (!properties || typeof properties !== 'object') {
    return rawData;
  }

  const coerced: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawData)) {
    const propertySchema = properties[key];
    coerced[key] = coerceValueForSchema(value as JsonPrimitive, propertySchema);
  }
  return coerced;
};

export const __testing__ = {
  coerceToNumber,
  coerceToInteger,
  coerceToBoolean,
  coerceToArrayOfStrings,
  coerceValueForSchema,
  extractEntities,
  findEntityByType,
};
